import { useEffect, useMemo, useState } from 'react'
import { KeyRound, Pencil, Plus, PowerOff, ShieldCheck } from 'lucide-react'
import Button from '@/components/button'
import ChipSelect from '@/components/chip-select'
import ConfirmDialog from '@/components/confirm-dialog'
import DataTable from '@/components/data-table'
import type { Column } from '@/components/data-table'
import FilterBar from '@/components/filter-bar'
import PageHeader from '@/components/page-header'
import StatusBadge from '@/components/status-badge'
import {
  useAtualizarAdministradorMutation,
  useAtualizarSituacaoAdministradorMutation,
  useCriarAdministradorMutation,
  useResetarSenhaAdministradorMutation,
} from '@/http/mutations/administradores'
import { useAdministradoresQuery } from '@/http/queries/administradores'
import type { AdministradorResumo } from '@/types/administrador'
import { STATUS_ADMINISTRADOR, situacaoAdministradorBadges } from '@/types/administrador'
import { SITUACAO_USUARIO } from '@/types/domain'
import { useAuth } from '@/utility/context/auth-context'
import { ACAO } from '@/utility/permissions'
import { useDebouncedValue } from '@/utility/use-debounced-value'
import { orDash } from '@/utility/Utils'
import { contarFiltrosDoPainel, filtroVazio, montarChips, paraFiltroApi } from './filtro'
import type { FiltroAdministradores } from './filtro'
import AdministradorFormModal from './form'
import type { AdministradorFormValues } from './form'
import styles from './style.module.css'

const administradorEstaAtivo = (administrador: AdministradorResumo): boolean =>
  administrador.situacao === SITUACAO_USUARIO.Ativo

const AdministradoresList = () => {
  const { can, user } = useAuth()
  const podeGerenciar = can(ACAO.GerenciarAdministradores)

  const [busca, setBusca] = useState('')
  const [aplicado, setAplicado] = useState<FiltroAdministradores>(filtroVazio)
  const [rascunho, setRascunho] = useState<FiltroAdministradores>(filtroVazio)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  const [administradorEmEdicao, setAdministradorEmEdicao] = useState<AdministradorResumo | null>(null)
  const [modalAberto, setModalAberto] = useState(false)
  const [alvoSituacao, setAlvoSituacao] = useState<AdministradorResumo | null>(null)
  const [alvoSenha, setAlvoSenha] = useState<AdministradorResumo | null>(null)

  const ehOProprio = (administrador: AdministradorResumo) => administrador.id === user?.usuarioBackofficeId

  const buscaDebounced = useDebouncedValue(busca)

  useEffect(() => {
    setAplicado((atual) => (atual.busca === buscaDebounced ? atual : { ...atual, busca: buscaDebounced }))
    setPageNumber(1)
  }, [buscaDebounced])

  const query = useMemo(
    () => ({ ...paraFiltroApi(aplicado, pageSize), pageNumber }),
    [aplicado, pageNumber, pageSize],
  )

  const { data: page, error, isFetching, refetch } = useAdministradoresQuery(query)
  const criarAdministrador = useCriarAdministradorMutation(() => setModalAberto(false))
  const atualizarAdministrador = useAtualizarAdministradorMutation(() => setModalAberto(false))
  const atualizarSituacao = useAtualizarSituacaoAdministradorMutation()
  const resetarSenha = useResetarSenhaAdministradorMutation()

  const efetivar = (proximo: FiltroAdministradores) => {
    setAplicado(proximo)
    setRascunho(proximo)
    setBusca(proximo.busca)
    setPageNumber(1)
  }

  const alterarRascunho = <C extends keyof FiltroAdministradores>(
    campo: C,
    valor: FiltroAdministradores[C],
  ) => {
    setRascunho((atual) => ({ ...atual, [campo]: valor }))
  }

  const alternarSituacao = (valores: string[]) => {
    const nova = valores.at(-1) as FiltroAdministradores['situacao'] | undefined
    alterarRascunho('situacao', nova ?? STATUS_ADMINISTRADOR.Todas)
  }

  const abrirCriacao = () => {
    setAdministradorEmEdicao(null)
    setModalAberto(true)
  }

  const abrirEdicao = (administrador: AdministradorResumo) => {
    setAdministradorEmEdicao(administrador)
    setModalAberto(true)
  }

  const salvar = (valores: AdministradorFormValues) => {
    if (administradorEmEdicao) {
      if (!administradorEmEdicao.id) return

      atualizarAdministrador.mutate({
        id: administradorEmEdicao.id,
        nome: valores.nome,
        email: valores.email,
        telefone: valores.telefone,
        telefoneDDI: valores.telefoneDDI,
      })
      return
    }

    criarAdministrador.mutate({
      nome: valores.nome,
      email: valores.email,
      telefone: valores.telefone,
      telefoneDDI: valores.telefoneDDI,
    })
  }

  const colunaAcoes: Column<AdministradorResumo> = {
    key: 'acoes',
    header: '',
    align: 'right',
    width: '200px',
    render: (administrador) => (
      <div className={styles.acoes}>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Editar administrador"
          title="Editar"
          onClick={() => abrirEdicao(administrador)}
        >
          <Pencil size={16} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Redefinir senha"
          title="Redefinir senha"
          onClick={() => setAlvoSenha(administrador)}
        >
          <KeyRound size={16} />
        </Button>
        {!ehOProprio(administrador) && (
          <Button
            variant={administradorEstaAtivo(administrador) ? 'danger' : 'success'}
            size="sm"
            iconLeft={
              administradorEstaAtivo(administrador) ? <PowerOff size={14} /> : <ShieldCheck size={14} />
            }
            onClick={() => setAlvoSituacao(administrador)}
          >
            {administradorEstaAtivo(administrador) ? 'Inativar' : 'Reativar'}
          </Button>
        )}
      </div>
    ),
  }

  const columns: Column<AdministradorResumo>[] = [
    {
      key: 'nome',
      header: 'Nome',
      render: (administrador) => (
        <div className={styles.stack}>
          <strong>{orDash(administrador.nome)}</strong>
          <span className={styles.muted}>{orDash(administrador.email)}</span>
        </div>
      ),
    },
    {
      key: 'telefone',
      header: 'Telefone',
      render: (administrador) =>
        administrador.telefone1 ? `+${orDash(administrador.telefone1DDI)} ${administrador.telefone1}` : '-',
    },
    {
      key: 'situacao',
      header: 'Situação',
      render: (administrador) => (
        <StatusBadge
          label={administradorEstaAtivo(administrador) ? 'Ativo' : 'Inativo'}
          tone={administradorEstaAtivo(administrador) ? 'success' : 'neutral'}
        />
      ),
    },
    ...(podeGerenciar ? [colunaAcoes] : []),
  ]

  return (
    <>
      <PageHeader
        title="Administradores"
        subtitle="Cadastro dos administradores do Admin IntegraDoor"
        actions={
          podeGerenciar ? (
            <Button iconLeft={<Plus size={16} />} onClick={abrirCriacao}>
              Novo administrador
            </Button>
          ) : null
        }
      />

      <FilterBar
        searchValue={busca}
        onSearchChange={setBusca}
        onSearchClear={() => efetivar({ ...aplicado, busca: '' })}
        searchPlaceholder="Buscar por nome ou email"
        searchLabel="Buscar administradores"
        isSearching={isFetching && busca !== buscaDebounced}
        chips={montarChips(aplicado, (campo) => efetivar({ ...aplicado, [campo]: filtroVazio[campo] }))}
        activeCount={contarFiltrosDoPainel(aplicado)}
        onApply={() => efetivar({ ...rascunho, busca: aplicado.busca })}
        onClear={() => efetivar(filtroVazio)}
        advanced={null}
        quickFilters={
          <ChipSelect
            options={situacaoAdministradorBadges}
            value={rascunho.situacao === STATUS_ADMINISTRADOR.Todas ? [] : [rascunho.situacao]}
            onChange={alternarSituacao}
            ariaLabel="Situação do administrador"
          />
        }
      />

      <DataTable
        columns={columns}
        page={page}
        getRowKey={(administrador) => administrador.id ?? ''}
        isLoading={isFetching}
        error={error}
        onRetry={() => void refetch()}
        onPageChange={setPageNumber}
        pageSize={pageSize}
        onPageSizeChange={(novoTamanho) => {
          setPageSize(novoTamanho)
          setPageNumber(1)
        }}
        emptyTitle="Nenhum administrador encontrado"
        emptyDescription="Ajuste os filtros e tente novamente."
      />

      <AdministradorFormModal
        isOpen={modalAberto}
        toggle={() => setModalAberto(false)}
        administrador={administradorEmEdicao}
        isLoading={criarAdministrador.isPending || atualizarAdministrador.isPending}
        onSubmit={salvar}
      />

      <ConfirmDialog
        isOpen={Boolean(alvoSituacao)}
        toggle={() => setAlvoSituacao(null)}
        title={
          alvoSituacao && administradorEstaAtivo(alvoSituacao) ? 'Inativar administrador' : 'Reativar administrador'
        }
        message={
          alvoSituacao && administradorEstaAtivo(alvoSituacao)
            ? `O acesso de ${orDash(alvoSituacao?.nome)} ao backoffice será negado a partir do próximo login. O histórico de ações que ele já executou continua preservado.`
            : `${orDash(alvoSituacao?.nome)} voltará a ter acesso ao backoffice.`
        }
        confirmLabel={
          alvoSituacao && administradorEstaAtivo(alvoSituacao) ? 'Inativar administrador' : 'Reativar administrador'
        }
        tone={alvoSituacao && administradorEstaAtivo(alvoSituacao) ? 'danger' : 'success'}
        isLoading={atualizarSituacao.isPending}
        onConfirm={() => {
          if (!alvoSituacao?.id) return

          atualizarSituacao.mutate(
            { administradorId: alvoSituacao.id, ativar: !administradorEstaAtivo(alvoSituacao) },
            { onSuccess: () => setAlvoSituacao(null) },
          )
        }}
      />

      <ConfirmDialog
        isOpen={Boolean(alvoSenha)}
        toggle={() => setAlvoSenha(null)}
        title="Redefinir senha"
        message={`A senha de ${orDash(alvoSenha?.nome)} volta para a senha padrão. Ele vai precisar trocá-la no próximo acesso.`}
        confirmLabel="Redefinir senha"
        tone="danger"
        isLoading={resetarSenha.isPending}
        onConfirm={() => {
          if (!alvoSenha?.id) return

          resetarSenha.mutate(alvoSenha.id, { onSuccess: () => setAlvoSenha(null) })
        }}
      />
    </>
  )
}

export default AdministradoresList
